import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

import { Spinner } from '../spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bhanzu-orange disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      color: {
        primary: '',
        secondary: '',
        dark: '',
        light: '',
      },
      variant: {
        // Bhanzu_Legend - Primary orange button
        legend:
          'bg-bhanzu-orange text-white shadow hover:bg-bhanzu-orange-dark active:bg-bhanzu-orange-dark disabled:bg-magical-moonlight-d1 disabled:text-magical-moonlight-white',
        // Bhanzu_Contained - Blue contained button
        contained:
          'bg-blue-chaos text-white shadow hover:bg-blue-chaos-dark active:bg-blue-chaos-dark disabled:bg-magical-moonlight-d1 disabled:text-magical-moonlight-white',
        // Bhanzu_Outline - Outline button
        outline:
          'border-2 border-blue-chaos-dark bg-transparent text-blue-chaos-dark shadow-sm hover:bg-blue-chaos-dark hover:text-white active:bg-bhanzu-orange-dark active:text-white disabled:border-magical-moonlight-d1 disabled:text-magical-moonlight-d1',
        // Bhanzu_Text - Text only button
        text: 'bg-transparent text-bhanzu-orange hover:bg-bhanzu-orange-light-cardbg active:bg-bhanzu-orange-light-cardbg disabled:text-magical-moonlight-d1',
        // Legacy variants for backward compatibility
        default:
          'bg-bhanzu-orange text-white shadow hover:bg-bhanzu-orange-dark',
        destructive:
          'bg-fusion-red text-white shadow-sm hover:bg-fusion-red-dark',
        secondary:
          'bg-blue-chaos text-white shadow-sm hover:bg-blue-chaos-dark',
        ghost: 'hover:bg-bhanzu-orange-light-cardbg hover:text-bhanzu-orange',
        link: 'text-bhanzu-orange underline-offset-4 hover:underline hover:text-bhanzu-orange-dark',
        success:
          'bg-success-green text-white shadow-sm hover:bg-success-green-dark',
        warning:
          'bg-warning-yellow text-magical-moonlight shadow-sm hover:bg-warning-yellow-dark',
        raised:
          'relative bg-transparent text-white font-bold rounded-full transition-all duration-200 hover:translate-y-[-2px] active:translate-y-[2px]',
        'raised-jumbo':
          'relative bg-transparent text-white font-bold rounded-lg transition-all duration-200 hover:translate-y-[-2px] active:translate-y-[2px]',
      },
      size: {
        // Large button - 16px font, 700 weight, 24px line-height
        lg: 'h-12 px-8 py-3 text-base font-bold leading-6',
        // Jumbo button - larger height for raised-jumbo variant
        jumbo: 'h-14 px-8 py-4 text-base font-bold leading-6',
        // Medium button - 14px font, 700 weight, 22px line-height
        md: 'h-10 px-6 py-2 text-sm font-bold leading-[22px]',
        // Small button - 12px font, 700 weight, 20px line-height
        sm: 'h-8 px-4 py-1 text-xs font-bold leading-5',
        // Icon button
        icon: 'h-10 w-10',
        // Legacy sizes for backward compatibility
        default: 'h-10 px-6 py-2 text-sm font-bold leading-[22px]',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        color: 'dark',
        className:
          'border-magical-moonlight-d4 text-magical-moonlight-d4 hover:bg-magical-moonlight-d4 hover:text-white',
      },
      {
        variant: 'outline',
        color: 'light',
        className:
          'border-magical-moonlight-white text-magical-moonlight-white hover:bg-magical-moonlight-white hover:text-magical-moonlight-d4',
      },
      {
        variant: 'text',
        color: 'secondary',
        className:
          'bg-transparent text-blue-chaos active:bg-blue-chaos-light-cardbg disabled:text-magical-moonlight-d1',
      },
      {
        variant: 'link',
        color: 'secondary',
        className:
          'bg-transparent text-blue-chaos active:bg-blue-chaos-light-cardbg disabled:text-magical-moonlight-d1 hover:text-blue-chaos-dark',
      },
    ],
    defaultVariants: {
      variant: 'legend',
      size: 'md',
      fullWidth: false,
      color: 'primary',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?:
    | 'legend'
    | 'contained'
    | 'outline'
    | 'text'
    | 'default'
    | 'destructive'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'success'
    | 'warning'
    | 'raised'
    | 'raised-jumbo';
  size?: 'sm' | 'md' | 'lg' | 'jumbo' | 'icon' | 'default';
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  fullWidth?: boolean;
  isLoading?: boolean;
  color?: 'primary' | 'secondary' | 'dark' | 'light';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      icon,
      iconPosition = 'start',
      fullWidth,
      isLoading = false,
      disabled,
      color,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    const renderIcon = () => {
      if (!icon) return null;
      return (
        <span className="flex items-center justify-center w-6 h-6 shrink-0">
          {icon}
        </span>
      );
    };

    const renderContent = (): React.ReactNode => {
      if (isLoading) {
        return (
          <span className="relative flex items-center justify-center">
            <span className="opacity-0">{children}</span>
            <span className="absolute inset-0 flex items-center justify-center">
              <Spinner
                size={size === 'sm' ? 'sm' : 'md'}
                variant={variant === 'outline' ? 'primary' : 'light'}
              />
            </span>
          </span>
        );
      }

      if (!icon) return children;

      return (
        <>
          {iconPosition === 'start' && renderIcon()}
          {children ? <span>{children}</span> : null}
          {iconPosition === 'end' && renderIcon()}
        </>
      );
    };

    if (variant === 'raised') {
      return (
        <div className={cn('relative inline-block', fullWidth && 'w-full')}>
          {/* Bottom shadow layer */}
          <div className="absolute bg-bhanzu-orange-dark h-full left-[2px] rounded-full top-[3px] w-full" />
          {/* Top button layer */}
          <Comp
            className={cn(
              buttonVariants({ variant, size, fullWidth, color, className })
            )}
            ref={ref}
            disabled={disabled ?? isLoading}
            {...props}
          >
            <div className="absolute inset-0 bg-bhanzu-orange rounded-full border-2 border-bhanzu-orange-dark" />
            <span className="relative z-(--z-nominal) flex items-center gap-2">
              {renderContent()}
            </span>
          </Comp>
        </div>
      );
    }

    if (variant === 'raised-jumbo') {
      return (
        <div className={cn('relative inline-block', fullWidth && 'w-full')}>
          {/* Bottom shadow layer */}
          <div className="absolute bg-bhanzu-orange-dark h-full left-[6px] rounded-2xl top-[6px] w-full" />
          {/* Top button layer */}
          <Comp
            className={cn(
              buttonVariants({
                variant,
                size: size ?? 'jumbo',
                fullWidth,
                color,
                className,
              })
            )}
            ref={ref}
            disabled={disabled ?? isLoading}
            {...props}
          >
            <div className="absolute inset-0 bg-bhanzu-orange rounded-2xl" />
            <span className="relative z-(--z-nominal) flex items-center gap-2">
              {renderContent()}
            </span>
          </Comp>
        </div>
      );
    }

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, fullWidth, color, className })
        )}
        ref={ref}
        disabled={disabled ?? isLoading}
        {...props}
      >
        {renderContent()}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export default Button;
export { buttonVariants };
